// utils/sellCenterStatus.js
//
// Frontend mirror of server_elakay/elakay_helpers/sellCenterStatus.js --
// front_elakay and server_elakay are separate git repos/build trees (no
// shared module boundary), so the constants/fallback logic are kept here in
// sync by hand rather than duplicated ad hoc inside SellingHubButtons.jsx or
// AnimationSettings.jsx. Keep both files' SELL_CENTER_ROLES/STATUSES/
// FEATURE_KEYS/LEGACY_DEFAULTS identical if either ever changes.
//
// Role keys are the lowercase form of exactly what
// includes/pageInside/Rolle.js's getCurrentElakayRole() returns.
export const SELL_CENTER_ROLES = ['seller', 'provider', 'associate', 'investor', 'admin', 'family'];

export const SELL_CENTER_STATUSES = ['active', 'soon', 'coming_back'];

export const SELL_CENTER_FEATURE_KEYS = [
  'sellProduct',
  'sellService',
  'dropshippingIn',
  'dropshippingOut',
  'providerProducerIn',
  'providerProducerOut',
  'addProviderProduct',
  'manageProviderProducts',
  'wholesale',
  'providerDropshipping',
  'providerFulfillment',
  'providerOrders',
];

// Phase 1's single global default per feature -- reproduced here so a
// feature with no per-role config yet (or an account with no resolvable
// role) resolves identically to before this per-role migration.
export const SELL_CENTER_LEGACY_DEFAULTS = {
  sellProduct: 'active',
  sellService: 'soon',
  dropshippingIn: 'active',
  dropshippingOut: 'active',
  providerProducerIn: 'active',
  providerProducerOut: 'soon',
  addProviderProduct: 'active',
  manageProviderProducts: 'active',
  wholesale: 'active',
  providerDropshipping: 'active',
  providerFulfillment: 'active',
  providerOrders: 'active',
};

// Mirror of the backend's SELL_CENTER_FEATURE_FALLBACK: a Provider card
// nobody customized yet resolves through the older feature it used to borrow.
export const SELL_CENTER_FEATURE_FALLBACK = {
  addProviderProduct: 'sellProduct',
  manageProviderProducts: 'providerProducerIn',
  wholesale: 'providerProducerIn',
  providerDropshipping: 'dropshippingIn',
  providerFulfillment: 'providerProducerIn',
};

// Single registry shared by AnimationSettings (admin matrix) and
// SellingHubButtons (cards): same feature keys, group, route and the
// Rolle.js capability that gates the card. Status never grants permission --
// `capability` (null = none beyond the section's own gate) is checked first.
// Route/capability are only read for the Provider group; Seller cards keep
// their existing per-card wiring in SellingHubButtons.
export const SELL_CENTER_GROUPS = [
  { id: 'seller', labelKey: 'sellCenterGroupSeller', labelFallback: 'Seller / Shared Activities' },
  { id: 'provider', labelKey: 'sellCenterGroupProvider', labelFallback: 'Provider / Producer Activities' },
];

export const SELL_CENTER_FEATURES = [
  { key: 'sellProduct', group: 'seller', labelKey: 'sellCenterFeatureSellProduct', labelFallback: 'Sell Your Product' },
  { key: 'sellService', group: 'seller', labelKey: 'sellCenterFeatureSellService', labelFallback: 'Sell My Service' },
  { key: 'dropshippingIn', group: 'seller', labelKey: 'sellCenterFeatureDropshippingIn', labelFallback: 'Dropshipping In' },
  { key: 'dropshippingOut', group: 'seller', labelKey: 'sellCenterFeatureDropshippingOut', labelFallback: 'Dropshipping Out' },
  { key: 'providerProducerIn', group: 'seller', labelKey: 'sellCenterFeatureProviderProducerIn', labelFallback: 'Provider / Producer In' },
  { key: 'providerProducerOut', group: 'seller', labelKey: 'sellCenterFeatureProviderProducerOut', labelFallback: 'Provider / Producer Out' },
  { key: 'addProviderProduct', group: 'provider', labelKey: 'sellCenterFeatureAddProviderProduct', labelFallback: 'Add Provider Product', route: '/provider/products/add', capability: 'canCreateProviderProduct' },
  { key: 'manageProviderProducts', group: 'provider', labelKey: 'sellCenterFeatureManageProviderProducts', labelFallback: 'Manage Provider Products', route: '/provider/products', capability: 'canManageProviderProducts' },
  { key: 'wholesale', group: 'provider', labelKey: 'sellCenterFeatureWholesale', labelFallback: 'Wholesale', route: '/provider/products?mode=wholesale', capability: 'canManageWholesale' },
  { key: 'providerDropshipping', group: 'provider', labelKey: 'sellCenterFeatureProviderDropshipping', labelFallback: 'Provider Dropshipping', route: '/provider/products?mode=dropshipping', capability: 'canManageProviderDropshipping' },
  { key: 'providerFulfillment', group: 'provider', labelKey: 'sellCenterFeatureProviderFulfillment', labelFallback: 'Fulfillment Settings', route: '/provider/products?mode=fulfillment', capability: 'canManageProviderProducts' },
  { key: 'providerOrders', group: 'provider', labelKey: 'sellCenterFeatureProviderOrders', labelFallback: 'Provider Orders', route: '/provider/orders', capability: null },
];

export const getSellCenterFeature = (key) => SELL_CENTER_FEATURES.find((f) => f.key === key);

export const normalizeSellCenterRole = (roleName) => {
  const lowered = String(roleName || '').trim().toLowerCase();
  if (lowered === 'producer') return 'provider';
  if (lowered === 'administrator') return 'admin';
  return SELL_CENTER_ROLES.includes(lowered) ? lowered : null;
};

export const normalizeProductsPerProvider = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? Math.min(16, Math.max(2, parsed)) : 8;
};

// The single function every Sell Center card getter calls -- never repeat
// this fallback chain inline per card (spec: avoid a giant condition
// duplicated 6 times). `allSettings` is infoSource.allSettings (already
// loaded once for the whole app -- this never triggers its own fetch).
// `role` is getCurrentElakayRole(infoSource)'s output (or already-normalized).
export const getSellCenterFeatureStatus = (allSettings, featureKey, role) => {
  const feature = allSettings?.sellCenter?.[featureKey];
  const normalizedRole = normalizeSellCenterRole(role);

  if (normalizedRole) {
    const perRole = feature?.statusByRole?.[normalizedRole];
    if (SELL_CENTER_STATUSES.includes(perRole)) return perRole;
  }

  const legacyFlat = feature?.status;
  if (SELL_CENTER_STATUSES.includes(legacyFlat)) return legacyFlat;

  const parentKey = SELL_CENTER_FEATURE_FALLBACK[featureKey];
  if (parentKey) return getSellCenterFeatureStatus(allSettings, parentKey, role);

  return SELL_CENTER_LEGACY_DEFAULTS[featureKey] || 'active';
};
